import { useEffect, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const CLASS_TONE = {
  confirmed_fact: "bg-emerald-100 text-emerald-700", imported_tax_fact: "bg-sky-100 text-sky-700",
  client_statement: "bg-violet-100 text-violet-700", staff_statement: "bg-indigo-100 text-indigo-700",
  third_party_statement: "bg-slate-100 text-slate-600", inference: "bg-amber-100 text-amber-700",
  unresolved_conflict: "bg-rose-100 text-rose-700", unknown: "bg-slate-100 text-slate-500",
  historical_fact: "bg-slate-100 text-slate-600", possibly_current_requires_reconfirmation: "bg-orange-100 text-orange-700",
};

/* ─── Service Edit Dialog ─────────────────────────────────────────────────── */
const EMPTY_FORM = (svc) => ({
  service_name:    svc.service_name    || "",
  service_code:    svc.service_code    || "",
  catalog_id:      svc.catalog_id      || "",
  tax_year:        svc.tax_year        || "",
  service_period:  svc.service_period  || "",
  completed_date:  svc.completed_date  ? svc.completed_date.slice(0, 10) : "",
  amount_paid:     svc.amount_paid     != null ? String(svc.amount_paid) : "",
  amount_charged:  svc.amount_charged  != null ? String(svc.amount_charged) : "",
  currency:        svc.currency        || "USD",
  description:     svc.description     || "",
  internal_note:   svc.internal_note   || "",
  source_reference: svc.source_reference || "",
});

const Field = ({ label, name, form, onChange, type = "text", required = false, hint, testId }) => (
  <div>
    <label className="block text-xs font-medium text-slate-600 mb-0.5">
      {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
    {type === "textarea" ? (
      <textarea
        rows={2}
        value={form[name]}
        onChange={(e) => onChange(name, e.target.value)}
        data-testid={testId}
        className="w-full text-xs border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-violet-400 resize-none"
      />
    ) : (
      <input
        type={type}
        value={form[name]}
        onChange={(e) => onChange(name, e.target.value)}
        data-testid={testId}
        className="w-full text-xs border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-violet-400"
      />
    )}
    {hint && <p className="text-[10px] text-slate-400 mt-0.5">{hint}</p>}
  </div>
);

const ServiceEditDialog = ({ svc, clientId, onSaved, onClose }) => {
  const [form, setForm] = useState(EMPTY_FORM(svc));
  const [busy, setBusy] = useState(false);

  const set = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const handleSave = async () => {
    setBusy(true);
    try {
      // Send all editable fields; backend detects actual changes.
      const payload = {
        service_name:    form.service_name    || null,
        service_code:    form.service_code    || null,
        catalog_id:      form.catalog_id      || null,
        tax_year:        form.tax_year        || null,
        service_period:  form.service_period  || null,
        completed_date:  form.completed_date  || null,
        amount_paid:     form.amount_paid     !== "" ? parseFloat(form.amount_paid) : null,
        amount_charged:  form.amount_charged  !== "" ? parseFloat(form.amount_charged) : null,
        currency:        form.currency        || null,
        description:     form.description     || null,
        internal_note:   form.internal_note   || null,
        source_reference: form.source_reference || null,
      };
      const { data: updated } = await api.patch(
        `/historical-summary/${clientId}/services/${svc.id}`,
        payload,
      );
      toast.success("Service record corrected");
      onSaved(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Correction failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="svc-edit-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold text-slate-800">
            Correct Historical Service Record
          </DialogTitle>
          <p className="text-[10px] text-slate-400 mt-0.5">
            Provenance fields (import date, importer, source) are immutable.
          </p>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 mt-2">
          <div className="col-span-2">
            <Field label="Service Name" name="service_name" form={form} onChange={set}
                   required hint="e.g. US Form 1040" testId="svc-edit-name" />
          </div>
          <Field label="Service Code" name="service_code" form={form} onChange={set} testId="svc-edit-code" />
          <Field label="Catalog ID"   name="catalog_id"   form={form} onChange={set} testId="svc-edit-catalog" />
          <Field label="Tax Year"     name="tax_year"     form={form} onChange={set} testId="svc-edit-tax-year" />
          <Field label="Service Period" name="service_period" form={form} onChange={set} testId="svc-edit-period" />
          <Field label="Completed Date" name="completed_date" form={form} onChange={set}
                 type="date" testId="svc-edit-completed-date" />
          <Field label="Currency (ISO-3)" name="currency" form={form} onChange={set}
                 hint="e.g. USD, THB, EUR" testId="svc-edit-currency" />
          <Field label="Amount Paid"     name="amount_paid"    form={form} onChange={set}
                 type="number" required hint="Required, non-negative" testId="svc-edit-amount-paid" />
          <div className="col-span-2">
            <Field label="Amount Charged" name="amount_charged" form={form} onChange={set}
                   type="number" hint="Optional — clear to remove" testId="svc-edit-amount-charged" />
          </div>
          <div className="col-span-2">
            <Field label="Description" name="description" form={form} onChange={set}
                   type="textarea" testId="svc-edit-description" />
          </div>
          <div className="col-span-2">
            <Field label="Internal Note" name="internal_note" form={form} onChange={set}
                   type="textarea" testId="svc-edit-internal-note" />
          </div>
          <div className="col-span-2">
            <Field label="Source Reference" name="source_reference" form={form} onChange={set}
                   testId="svc-edit-source-ref" />
          </div>
        </div>

        <DialogFooter className="mt-4 gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            data-testid="svc-edit-cancel"
            className="text-xs px-3 py-1.5 rounded border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={busy}
            data-testid="svc-edit-save"
            className="inline-flex items-center gap-1 text-xs font-medium bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white rounded px-3 py-1.5"
          >
            {busy ? <Icons.Loader2 className="h-3 w-3 animate-spin" /> : <Icons.Check className="h-3 w-3" />}
            Save Correction
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ─── Main Card ───────────────────────────────────────────────────────────── */
export const HistoricalSummaryCard = ({ clientId }) => {
  const [enabled, setEnabled] = useState(null);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selVersion, setSelVersion] = useState(null);
  const [editingSvc, setEditingSvc] = useState(null);   // service record being edited

  const load = useCallback(async () => {
    try {
      const cfg = (await api.get("/historical-import/config")).data;
      if (!cfg.enabled || !cfg.can_view_summary) { setEnabled(false); return; }
      setEnabled(true);
      const { data } = await api.get(`/historical-summary/${clientId}`);
      setData(data); setSelVersion(data.current?.version || null);
    } catch (e) {
      if (e?.response?.status === 404) setEnabled(false); else setEnabled(false);
    }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      await api.post(`/historical-summary/${clientId}/generate`, {});
      toast.success("Historical Relationship Summary generated");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed; previous version preserved");
    } finally { setBusy(false); }
  };
  const openSource = async (s) => {
    try { await api.post(`/historical-summary/${clientId}/source-open`, { source_id: s.source_id, source_type: s.type }); } catch {}
    toast.info(`${s.type}: ${s.label || s.source_id}`);
  };
  const viewVersion = async (v) => {
    setSelVersion(v);
    if (v === data.current?.version) return;
    try {
      const { data: ver } = await api.get(`/historical-summary/${clientId}/versions/${v}`);
      setData({ ...data, current: ver });
    } catch (e) {
      if (e?.response?.status === 403) setData({ ...data, current: { version: v, restricted: true, message: e.response.data?.detail } });
    }
  };

  /* Service saved callback — update in-place and re-sort. */
  const handleSvcSaved = (updated) => {
    setEditingSvc(null);
    setData((prev) => {
      if (!prev) return prev;
      const services = prev.services.map((s) => s.id === updated.id ? { ...s, ...updated } : s);
      // Re-apply canonical sort: tax_year DESC → completed_date DESC → id DESC (stable fallback)
      services.sort((a, b) => {
        const ty = (b.tax_year || "") > (a.tax_year || "") ? 1 : (b.tax_year || "") < (a.tax_year || "") ? -1 : 0;
        if (ty !== 0) return ty;
        const cd = (b.completed_date || "") > (a.completed_date || "") ? 1 : (b.completed_date || "") < (a.completed_date || "") ? -1 : 0;
        if (cd !== 0) return cd;
        return (b.id || "") > (a.id || "") ? 1 : -1;
      });
      return { ...prev, services };
    });
  };

  if (enabled === false || enabled === null) return null;
  const cur = data?.current;
  const cov = cur?.coverage || {};
  const covParts = [
    [cov.email_threads, "email threads"], [cov.messages, "messages"], [cov.documents, "documents"],
    [cov.timeline_events, "timeline events"], [cov.notes, "notes"], [cov.tax_profile_facts, "imported tax facts"],
  ].filter(([n]) => n).map(([n, l]) => `${n} ${l}`);

  return (
    <div className="rounded-xl border border-slate-200 border-l-4 border-l-violet-500 bg-white shadow-sm p-4 mb-5" data-testid="historical-summary-card">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icons.Sparkles className="h-4 w-4 text-violet-500" />
          <h3 className="font-semibold text-charcoal text-sm">Allison Historical Relationship Summary</h3>
        </div>
        <div className="flex items-center gap-2">
          {data?.versions?.length > 0 && (
            <select className="text-xs border border-slate-300 rounded px-1.5 py-1" value={selVersion || ""} onChange={(e) => viewVersion(parseInt(e.target.value))} data-testid="hs-version-select">
              {data.versions.map((v) => <option key={v.version} value={v.version}>v{v.version}{v.is_current ? " (current)" : ""} · {v.status}</option>)}
            </select>
          )}
          {data?.can_generate && (
            <button onClick={generate} disabled={busy} data-testid="hs-generate" className="inline-flex items-center gap-1 text-xs font-medium bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white rounded-lg px-3 py-1.5">
              {busy ? <Icons.Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icons.Wand2 className="h-3.5 w-3.5" />}{cur ? "Re-run" : "Generate"}
            </button>
          )}
        </div>
      </div>

      {busy && <p className="text-xs text-violet-600 mb-2" data-testid="hs-generating">Generating Historical Relationship Summary…</p>}

      {data?.freshness && data.freshness !== "no_summary" && (
        <div className={`text-[10px] mb-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${data.freshness === "current" ? "bg-emerald-50 text-emerald-700" : "bg-orange-50 text-orange-700"}`} data-testid="hs-freshness">
          <Icons.History className="h-3 w-3" />{data.freshness === "current" ? "Current" : "New historical information available — consider Re-run"}
        </div>
      )}

      {cur && cur.restricted ? (
        <p className="text-xs text-rose-600" data-testid="hs-restricted">{cur.message}</p>
      ) : !cur ? (
        <p className="text-xs text-slate-400">No summary generated yet. This reads only reconstructed historical records and never modifies anything.</p>
      ) : (
        <div data-testid="hs-content">
          <div className="text-[10px] text-slate-400 mb-2">v{cur.version} · {(cur.generated_at || "").slice(0, 16).replace("T", " ")} · by {cur.requested_by_name || "staff"} · {cur.model}</div>
          <p className="text-sm text-slate-700 whitespace-pre-line mb-3">{cur.output?.overview}</p>
          {(cur.output?.sections || []).map((sec, i) => (
            <div key={i} className="mb-2">
              <div className="text-xs font-semibold text-slate-600">{sec.title}</div>
              <ul className="mt-1 space-y-1">
                {(sec.conclusions || []).map((c, j) => (
                  <li key={j} className="text-xs text-slate-700 flex flex-wrap items-center gap-1">
                    <span>{c.text}</span>
                    <span className={`text-[9px] uppercase px-1 rounded ${CLASS_TONE[c.classification] || "bg-slate-100"}`}>{(c.classification || "").replace(/_/g, " ")}</span>
                    {(c.source_ids || []).map((sid) => {
                      const s = (cur.sources || []).find((x) => x.source_id === sid);
                      return s ? <button key={sid} onClick={() => openSource(s)} className="text-[9px] text-blue-600 hover:underline" data-testid={`hs-source-${sid}`}>↳ {s.type}</button> : null;
                    })}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {cur.output?.outstanding_questions?.length > 0 && (
            <div className="mt-2"><div className="text-xs font-semibold text-slate-600">Outstanding historical questions</div>
              <ul className="text-xs text-slate-600 list-disc pl-4">{cur.output.outstanding_questions.map((q, i) => <li key={i}>{q}</li>)}</ul></div>
          )}
          {cur.restricted_note && <p className="text-[10px] text-amber-600 mt-2">{cur.restricted_note}</p>}
          <div className="mt-3 pt-2 border-t border-slate-100 text-[10px] text-slate-400" data-testid="hs-source-footer">
            Source coverage: {covParts.join(" · ") || "—"}
          </div>
          <div className="mt-1 text-[10px] text-slate-400 italic flex items-center gap-1"><Icons.Info className="h-3 w-3" /> {cur.advisory || data.advisory}</div>
        </div>
      )}

      {/* Phase 3A/3C: Historical Services — staff read/edit, no billing actions */}
      {data?.services?.length > 0 && (
        <div className="mt-4 pt-3 border-t border-slate-100" data-testid="hs-services-section">
          <div className="flex items-center gap-1.5 mb-2">
            <Icons.Receipt className="h-3.5 w-3.5 text-slate-400" />
            <span className="text-xs font-semibold text-slate-600">Historical Services</span>
            <span className="ml-1 text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full" data-testid="hs-services-count">{data.services.length}</span>
          </div>
          <div className="space-y-1" data-testid="hs-services-list">
            {data.services.map((svc, i) => {
              const yearPeriod = [svc.tax_year, svc.service_period].filter(Boolean).join(" · ");
              const paidAmt = typeof svc.amount_paid === "number" ? svc.amount_paid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : svc.amount_paid;
              const chargedAmt = typeof svc.amount_charged === "number" ? svc.amount_charged.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : null;
              const showCharged = typeof svc.amount_charged === "number" && svc.amount_charged !== svc.amount_paid;
              return (
                <div key={svc.id || i} className="flex items-start justify-between gap-2 py-1.5 border-b border-slate-50 last:border-0" data-testid={`hs-service-${i}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium text-slate-700 truncate">
                      {svc.service_name}{svc.service_code && svc.service_code !== svc.service_name ? <span className="font-normal text-slate-400"> ({svc.service_code})</span> : null}
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      {yearPeriod || "—"}{svc.completed_date ? ` · ${svc.completed_date.slice(0, 10)}` : ""}
                    </div>
                    {svc.description && <div className="text-[10px] text-slate-400 mt-0.5 italic">{svc.description}</div>}
                  </div>
                  <div className="flex items-start gap-2 shrink-0">
                    <div className="text-right">
                      <div className="text-xs font-medium text-slate-700">{svc.currency || "USD"} {paidAmt}</div>
                      {showCharged && <div className="text-[10px] text-slate-400">Charged: {svc.currency || "USD"} {chargedAmt}</div>}
                      <span className="inline-block text-[9px] uppercase tracking-wide bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full mt-0.5">Completed · Paid</span>
                    </div>
                    {/* Phase 3C: per-row staff correction control */}
                    <button
                      onClick={() => setEditingSvc(svc)}
                      title="Correct this service record"
                      data-testid={`hs-service-edit-${i}`}
                      className="mt-0.5 p-1 rounded text-slate-400 hover:text-violet-600 hover:bg-violet-50 transition-colors"
                    >
                      <Icons.Pencil className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Service Edit Dialog (Phase 3C) */}
      {editingSvc && (
        <ServiceEditDialog
          svc={editingSvc}
          clientId={clientId}
          onSaved={handleSvcSaved}
          onClose={() => setEditingSvc(null)}
        />
      )}
    </div>
  );
};
